import {Controller} from "@hotwired/stimulus";
import { Modal, modalBackdrop } from 'flowbite';

export default class extends Controller {
  static values = { backdrop: String, open: Boolean, closable: Boolean };
  declare readonly closableValue: boolean;
  declare readonly openValue: boolean;
  declare readonly backdropValue: modalBackdrop;
  declare readonly hasBackdropValue: boolean;
  static targets = [ 'form', 'submit', 'cancel', 'close', 'focusInput', 'video' ];
  declare readonly videoTarget?: HTMLVideoElement;
  declare readonly hasVideoTarget: boolean;
  declare readonly focusInputTarget: HTMLInputElement;
  declare readonly hasFocusInputTarget: boolean;
  declare readonly hasFormTarget: boolean;
  declare readonly hasSubmitTarget: boolean;
  declare readonly formTarget: HTMLFormElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly cancelTarget: HTMLButtonElement;
  declare readonly hasCancelTarget: boolean;
  declare readonly closeTarget: HTMLButtonElement;
  declare readonly hasCloseTarget: boolean;
  modal: Modal;
  unloaded = false;

  connect() {
    // Init the instance and open
    this.initModal();
    // Setup form listeners
    this.initFormListeners();
    if (this.hasFocusInputTarget) this.focusInputTarget.focus();
  }

  initModal = () => {
    this.modal = new Modal(this.element as HTMLElement, {
      closable: !!this.closableValue,
      backdrop: this.hasBackdropValue ? this.backdropValue : 'static',
      backdropClasses: 'modal-backdrop bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-[51]',
      onHide: () => {
        if (this.openValue) this.unload();
        if (!this.openValue && this.hasVideoTarget) {
          this.videoTarget.pause();
          this.videoTarget.currentTime = 0;
        }
      }
    }, { override: true });
    if (this.openValue) this.modal.show();
  }

  // Invoked when a Form is mounted in the modal (this requires the form to connect to the 'form' controller)
  formLoad = e => {
    // Setup form stream listeners
    this.initFormListeners();
  }

  initFormListeners = () => {
    if (!this.hasFormTarget) return;
    // Remove any existing listeners
    this.formTarget.removeEventListener('turbo:submit-start', this.submitStart);
    this.formTarget.removeEventListener('turbo:submit-end', this.submitEnd);
    // Add new listeners
    this.formTarget.addEventListener('turbo:submit-start', this.submitStart);
    this.formTarget.addEventListener('turbo:submit-end', this.submitEnd);
  }

  onClose = e => this.unload();

  close = () => {
    if (this.modal) {
      try {
        this.modal.hide();
      }
      catch (e) {}
    }
  }

  unload = () => {
    if (this.unloaded) return;
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener('turbo:submit-start', this.submitStart);
      this.formTarget.removeEventListener('turbo:submit-end', this.submitEnd);
    }
    if (this.modal) {
      if (this.modal.isVisible()) this.close();
      try {
        this.modal.destroy();
      }
      catch (e) {}
      this.modal = null;
      try {
        this.element.remove();
      }
      catch (e) {}
    }
    this.unloaded = true;
  }

  disconnect() {
    this.unload();
  }

  toggleActions = (disabled: boolean) => {
    if (this.hasSubmitTarget) {
      if (disabled) this.submitTarget.setAttribute('disabled', 'disabled');
      else this.submitTarget.removeAttribute('disabled');
    }
    if (this.hasCancelTarget) {
      if (disabled) this.cancelTarget.setAttribute('disabled', 'disabled');
      else this.cancelTarget.removeAttribute('disabled');
    }
    if (this.hasCloseTarget) {
      if (disabled) this.closeTarget.setAttribute('disabled', 'disabled');
      else this.closeTarget.removeAttribute('disabled');
    }
  }

  submitStart = e => {
    if (!this.hasSubmitTarget) return;
    this.toggleActions(true);
    this.submitTarget.querySelector('.icon:not(.spinner)').classList.add('hidden');
    this.submitTarget.querySelector('.icon.spinner').classList.remove('hidden');
  }

  submitEnd = e => {
    this.toggleActions(false);
    this.submitTarget.querySelector('.icon:not(.spinner)').classList.remove('hidden');
    this.submitTarget.querySelector('.icon.spinner').classList.add('hidden');
    this.initFormListeners();
  }

  submit = () => {
    if (!this.hasFormTarget) return;
    if (this.formTarget.checkValidity()) this.formTarget.requestSubmit();
    else this.formTarget.reportValidity();
  }

  keydown = e => {
    if (e.target.id.includes('ts-control')) return;
    if (e.key === 'Enter' && this.hasFormTarget) this.submit();
  }
}